import { INFINITE_SCROLL_CARDS_PER_PAGE } from '@/constants';
import queryKeys from '@/constants/queryKeys';
import { TCustomAxiosResponse } from '@/services/api';
import { DirectoryPersonKind, setDirectoryStar } from '@/services/api/requests/directory-stars';
import { useAppStore } from '@/store/useAppStore';
import { TMemberCare } from '@/types/MemberCare.types';
import { TPeerSupportTeamMemberInfo } from '@/types/PeerSupport.types';
import { TProviderApi } from '@/types/Provider.types';
import { useToast } from '@chakra-ui/react';
import { InfiniteData, QueryClient, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

type Person = TProviderApi | TPeerSupportTeamMemberInfo | TMemberCare;
type Pages = InfiniteData<TCustomAxiosResponse<Person[]>, number>;

const personId = (person: Person): string => {
  if ('_id' in person) return person._id;
  if ('user_id' in person) return person.user_id;
  return person.id;
};

const listKey = (kind: DirectoryPersonKind, email: string) => {
  const root =
    kind === 'provider'
      ? queryKeys.providers.directory
      : kind === 'peer-support'
        ? queryKeys.peerSupport.members
        : queryKeys.memberCare.myOrganizationMemberCares;
  return [root, email, 'true', INFINITE_SCROLL_CARDS_PER_PAGE];
};

const patchStar = (pages: Pages | undefined, id: string, starred: boolean): Pages | undefined =>
  pages && {
    ...pages,
    pages: pages.pages.map((page) => ({
      ...page,
      data: page.data.map((person) => (personId(person) === id ? { ...person, starred } : person)),
    })),
  };

// Keep one refetch in flight for each directory. A second completed unstar
// during that refetch requests one more pass so its new server order is seen.
const refetches = new Map<string, { requested: boolean; promise: Promise<void> }>();
const refetchLoadedPages = (queryClient: QueryClient, queryKey: (string | number)[]) => {
  const key = JSON.stringify(queryKey);
  const running = refetches.get(key);
  if (running) {
    running.requested = true;
    return running.promise;
  }

  const state = { requested: true, promise: Promise.resolve() };
  refetches.set(key, state);
  state.promise = (async () => {
    try {
      while (state.requested) {
        state.requested = false;
        await queryClient.refetchQueries({ queryKey, exact: true, type: 'active' }, { cancelRefetch: false });
      }
    } finally {
      refetches.delete(key);
    }
  })();
  return state.promise;
};

export const useDirectoryStar = (kind: DirectoryPersonKind, id: string, starred: boolean) => {
  const email = useAppStore((state) => state.user?.email ?? '');
  const queryClient = useQueryClient();
  const toast = useToast();
  const queryKey = listKey(kind, email);
  const toggleInFlight = useRef(false);

  const mutation = useMutation({
    mutationFn: (next: boolean) => setDirectoryStar(kind, id, next),
    onMutate: async (next) => {
      await queryClient.cancelQueries({ queryKey, exact: true });
      const snapshot = queryClient.getQueryData<Pages>(queryKey);
      queryClient.setQueryData<Pages>(queryKey, (pages) => patchStar(pages, id, next));
      return { snapshot };
    },
    onError: (_error, _next, context) => {
      const previous = context?.snapshot?.pages.flatMap((page) => page.data).find((person) => personId(person) === id);
      if (previous) {
        queryClient.setQueryData<Pages>(queryKey, (pages) => patchStar(pages, id, Boolean(previous.starred)));
      }
      toast({ status: 'error', title: 'Couldn’t update star', duration: 2500, isClosable: true });
    },
    onSuccess: async (_response, next) => {
      await queryClient.invalidateQueries({ queryKey, exact: true, refetchType: 'none' });
      if (!next) {
        // Infinite-query refetch refreshes every loaded page after the row moves.
        await refetchLoadedPages(queryClient, queryKey);
      }
    },
  });

  return {
    isPending: mutation.isPending,
    toggle: () => {
      if (toggleInFlight.current) return;
      toggleInFlight.current = true;
      void mutation
        .mutateAsync(!starred)
        .catch(() => undefined)
        .finally(() => {
          toggleInFlight.current = false;
        });
    },
  };
};
